/**
 * Keep credentials in URLs out of what Sentry sends.
 *
 * Emailed sign-in and reset links carry their token as `#token=…`, and Sentry
 * records URLs in navigation breadcrumbs, the request and transaction name of
 * events, spans, and Replay's breadcrumb and span frames. `instrument.ts` strips
 * the token before Sentry starts; this is the second layer, for any URL that
 * still carries one. It also covers `access_token=`, `refresh_token=` and the
 * like, which end in `token=` too, and the `oobCode=` of Firebase email-action
 * links (`/login?mode=resetPassword&oobCode=…`), which act on the Firebase
 * account. Firebase's `apiKey=` is public and left as it is.
 */

const TOKEN_VALUE = /((?:token|oobCode)=)[^&#\s"'<>]+/gi;

const REDACTED = '[Filtered]';

export const redactTokens = (value: string): string => value.replace(TOKEN_VALUE, `$1${REDACTED}`);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const prototype = Object.getPrototypeOf(value) as unknown;
  return prototype === Object.prototype || prototype === null;
};

/**
 * A copy of `value` with `token=` values redacted in every string it holds.
 * Only strings, arrays and plain objects are walked; anything else is kept as
 * it is.
 */
export const redactTokensDeep = <T>(value: T, depth = 0): T => {
  if (typeof value === 'string') {
    return redactTokens(value) as T;
  }
  if (depth > 20) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item: unknown) => redactTokensDeep(item, depth + 1)) as T;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, redactTokensDeep(item, depth + 1)]),
    ) as T;
  }
  return value;
};

// rrweb's custom event type, which Replay uses for its breadcrumb and span
// frames. The DOM snapshots are left alone: they are large, and the address
// bar is not part of them.
const REPLAY_CUSTOM_EVENT = 5;

export const scrubReplayRecordingEvent = <T extends { type: number }>(event: T): T =>
  event.type === REPLAY_CUSTOM_EVENT ? redactTokensDeep(event) : event;
